import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import { Box, emotionStyled as styled } from '@stvgroup/stv-ui-components'
import useIsMobile from '../hooks/useIsMobile'
import {
  Image,
} from '..'
import PreviewPlayer from '../PreviewPlayer'
import PreviewPlayerControl from '../PreviewPlayerControl'
import { API_IMAGE_OBJECT } from '../../utils/types'
import HeroCTA from '../HeroCTA'
import HeroLiveTicker from '../HeroLiveTicker'
import { UPDATE_PREVIEW_PLAYER_PROPS, VOD_MESSAGE_TYPES } from '../../constants'
import { responsiveGlobalPadding } from '../../styles/layout'

const HeroGradientOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  bottom: -1,
  left: -1,
  right: 0,
  display: 'block',
  zIndex: 2,
  background: 'linear-gradient(0deg, #151515 15.1%, rgba(21, 21, 21, 0.4) 44.4%, rgba(21, 21, 21, 0.1) 100%)',
  [theme.breakpoints.up('sm')]: {
    background: 'linear-gradient(0deg, #151515 25%, rgba(21, 21, 21, 0.4) 54%, rgba(21, 21, 21, 0.1) 100%)',
  },
  [theme.breakpoints.up('md')]: {
    background: 'linear-gradient(90deg, #151515 0%, #151515 10%, rgba(21, 21, 21, 0) 55.31%), linear-gradient(0deg, #151515 0%, rgba(21, 21, 21, 0) 49.84%)',
  },
}))

const HeroCTAContainer = ({
  isVisible, className, children, isFirstRender = false,
}) => {
  const isMobile = useIsMobile()

  return (
    <Box
      className={`${className} ${isVisible && !isMobile && !isFirstRender ? 'slide-in' : ''}`}
      sx={{
        px: responsiveGlobalPadding,
        pt: { xs: '30vw', md: 0 },
        height: {
          xs: 'auto', sm: '500px', lg: '600px', xl: '700px',
        },
        opacity: isVisible ? 1 : 0,
        transform: isMobile || isFirstRender ? 'translateX(0)' : 'translateX(100%)',
        zIndex: 999,
        visibility: isVisible ? 'visible' : 'hidden',
        transition: 'opacity 300ms ease-out, visibility 300ms ease-out',
        display: 'flex',
        justifyContent: { xs: 'center', sm: 'flex-start' },
        width: '100%',
        alignItems: 'flex-end',
        pointerEvents: isVisible ? 'auto' : 'none',
        position: 'relative',

        '&.slide-in': {
          transform: 'translateX(0)',
          transition: 'opacity 300ms ease-out, transform 400ms cubic-bezier(0.0, 0, 0.2, 1), visibility 300ms ease-out',
        },
      }}
    >
      {children}
    </Box>
  )
}

HeroCTAContainer.propTypes = {
  isVisible: PropTypes.bool,
  className: PropTypes.string,
  children: PropTypes.node,
  isFirstRender: PropTypes.bool,
}

HeroCTAContainer.defaultProps = {
  isFirstRender: false,
}

const HeroCard = ({
  el,
  mobileHeroImage,
  webHeroImage,
  large,
  link,
  linkGuid,
  title,
  titleTextImage,
  overlay,
  hasHoverOverlay,
  newWindow,
  addClasses,
  sponsorship,
  forceAds,
  vipExclusive,
  vipProhibited,
  svodExclusive,
  svodProhibited,
  specialEvent,
  subGenre,
  recoID,
  rank,
  ctaButtonLink,
  ctaButtonText,
  ctaButtonType,
  ctaText,
  live,
  index,
  poster,
  page,
  showCTA,
  progmImages,
  name,
  longDescription,
  progmButton,
  progmButtonLink,
  availableEpisodes,
  audioDescribed,
  visuallySigned,
  isSubtitle,
  programmeDetails,
  isGuidance,
  prgmGenre,
  programmeTab,
  liveTab,
  hasInformation,
  playing,
  informationText,
  hasCompetitionText,
  competitionText,
  isVisible,
  autoplayShortform,
  inViewScroll,
  userPaused,
  firstRotation,
  isStvLiveProg,
  liveProgramme,
  showMyListButton,
  animateCTA = true,
  isFirstRender = false,
}) => {
  const Element = el
  const isMobile = useIsMobile()
  const [background, setBackground] = useState(webHeroImage)
  const [loadPlayer, setLoadPlayer] = useState(false)
  const [playVideo, setPlayVideo] = useState(false)
  const isLive = !!((liveProgramme?.guid === linkGuid || liveProgramme?.title === title))
  const psEnabled = useSelector(state => state?.featureFlags?.psEnabled)

  useEffect(() => {
    if (isMobile) {
      setBackground(mobileHeroImage)
    } else {
      setBackground(webHeroImage)
    }
  }, [isMobile, webHeroImage, mobileHeroImage])

  useEffect(() => {
    if (isVisible && inViewScroll && firstRotation && autoplayShortform?.videoId) {
      setLoadPlayer(true)
    } else {
      setLoadPlayer(false)
      setPlayVideo(false)
    }
  }, [isVisible, inViewScroll, firstRotation, autoplayShortform])

  useEffect(() => {
    let timer
    if (loadPlayer) {
      timer = setTimeout(() => {
        if (inViewScroll && !userPaused) {
          setPlayVideo(true)
        }
      }, 2000)
    }
    return () => clearTimeout(timer)
  }, [loadPlayer, inViewScroll, userPaused])

  useEffect(() => {
    if (!userPaused && playVideo) {
      window.postMessage({
        type: UPDATE_PREVIEW_PLAYER_PROPS,
        newData: { paused: false },
      }, '*')
    }
  }, [playVideo, userPaused])

  useEffect(() => {
    const handleVideoEnd = (event) => {
      if (event.data.type === VOD_MESSAGE_TYPES.CONTENT_HAS_ENDED) {
        setPlayVideo(false)
      }
    }
    window.addEventListener('message', handleVideoEnd)
    return () => {
      window.removeEventListener('message', handleVideoEnd)
    }
  }, [])

  const containerClass = 'heroCta-container'

  return (
    <Box
      component={Element}
      sx={{
        display: 'block',
        position: 'relative',
        zIndex: 1,
        transition: '400ms z-index',
        width: specialEvent ? '100%' : 'auto',
        maxWidth: specialEvent ? '1920px' : 'auto',
        height: '100%',
        '&:hover': {
          zIndex: 2,
        },
      }}
    >
      <Box
        component="figure"
        className="figure"
        sx={{
          m: 0,
          position: 'relative',
          transition: '100ms transform',
          transformOrigin: 'center',
          backfaceVisibility: 'hidden',
          willChange: 'transform',
          bgcolor: 'baseBg',
          borderRadius: '8px',
          zIndex: hasHoverOverlay && (page === 'home' || page === 'categories') ? 3 : 'auto',
          height: '100%',
          display: 'flex',
          alignItems: 'flex-end',
        }}
      >
        {(isStvLiveProg || ctaButtonType === 'watchLive' || isLive) && !psEnabled && (
          <HeroLiveTicker />
        )}
        <Box
          className="preview-container"
          sx={{
            width: { xs: '100%', md: '82%' },
            marginLeft: { xs: 0, md: '18%' },
            position: 'absolute',
            top: 0,
          }}
        >
          {!isMobile && loadPlayer && firstRotation && (
            <>
              <PreviewPlayer
                playing={playVideo}
                autoplayShortform={autoplayShortform}
              />
            </>
          )}
          <Box className="preview-image">
            <Image
              image={Object.keys(progmImages).length > 0 ? progmImages : background}
              sponsorship={sponsorship}
              sizes="100vw"
              priority={specialEvent}
            />
          </Box>
          <HeroGradientOverlay />
        </Box>
        {specialEvent && (
          <HeroCTAContainer
            className={containerClass}
            isVisible={showCTA && isVisible}
            isFirstRender={isFirstRender}
          >
            <HeroCTA
              sx={{
                width: '537px',
                maxWidth: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: { xs: 'center', md: 'flex-start' },
                textAlign: { xs: 'center', md: 'left' },
                mx: { xs: 'auto', md: '0' },

              }}
              title={title}
              titleTextImage={titleTextImage}
              text={longDescription || ctaText}
              index={index}
              buttonText={isStvLiveProg ? 'Watch live' : progmButton || ctaButtonText}
              buttonType={isStvLiveProg ? 'watchLive' : 'watchNow' || ctaButtonType}
              buttonLink={isStvLiveProg ? '/live/stv' : progmButtonLink || link}
              forceAds={forceAds}
              svodExclusive={svodExclusive}
              svodProhibited={svodProhibited}
              vipExclusive={vipExclusive}
              vipProhibited={vipProhibited}
              genre={prgmGenre}
              isGuidance={isGuidance}
              isSubtitle={isSubtitle}
              availableEpisodes={availableEpisodes}
              audioDescribed={audioDescribed}
              visuallySigned={visuallySigned}
              liveProgramme={isLive}
              programmeDetails={programmeDetails}
              hasInformation={hasInformation}
              hasCompetitionText={hasCompetitionText}
              competitionText={competitionText}
              isStvLiveProg={isStvLiveProg}
              informationText={informationText}
              showMyListButton={showMyListButton}
            />
            {!isMobile && loadPlayer && firstRotation && (
              <Box
                className="preview-controls"
                sx={{
                  position: 'absolute',
                  right: responsiveGlobalPadding,
                  display: 'flex',
                  gap: '16px',
                  zIndex: 10,
                }}
              >
                <PreviewPlayerControl
                  userPaused={userPaused}
                  shouldDisplay={playVideo === true}
                  controlType="play"
                  disabled={!inViewScroll}
                />
                <PreviewPlayerControl
                  userPaused={userPaused}
                  shouldDisplay={playVideo === true}
                  controlType="mute"
                />
              </Box>
            )}
          </HeroCTAContainer>
        )}
      </Box>
    </Box>
  )
}

HeroCard.propTypes = {
  el: PropTypes.string,
  progmImages: API_IMAGE_OBJECT,
  webHeroImage: API_IMAGE_OBJECT,
  mobileHeroImage: API_IMAGE_OBJECT,
  large: PropTypes.bool,
  link: PropTypes.string,
  linkGuid: PropTypes.string,
  title: PropTypes.string,
  titleTextImage: API_IMAGE_OBJECT,
  overlay: PropTypes.string,
  hasHoverOverlay: PropTypes.bool,
  newWindow: PropTypes.bool,
  addClasses: PropTypes.arrayOf(PropTypes.string),
  programmeDetails: PropTypes.shape({
    title: PropTypes.string,
    images: API_IMAGE_OBJECT,
    guid: PropTypes.string,
  }),
  sponsorship: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    image: PropTypes.shape({
      id: PropTypes.number,
      _filepath: PropTypes.string,
    }),
  }),
  forceAds: PropTypes.bool,
  svodExclusive: PropTypes.bool,
  svodProhibited: PropTypes.bool,
  vipExclusive: PropTypes.bool,
  vipProhibited: PropTypes.bool,
  specialEvent: PropTypes.bool,
  recoID: PropTypes.string,
  rank: PropTypes.number,
  ctaButtonLink: PropTypes.string,
  ctaButtonText: PropTypes.string,
  ctaButtonType: PropTypes.string,
  ctaText: PropTypes.string,
  live: PropTypes.bool,
  index: PropTypes.number,
  poster: PropTypes.bool,
  page: PropTypes.string,
  showCTA: PropTypes.bool,
  name: PropTypes.string,
  longDescription: PropTypes.string,
  progmButton: PropTypes.string,
  availableEpisodes: PropTypes.string,
  audioDescribed: PropTypes.bool,
  visuallySigned: PropTypes.bool,
  isSubtitle: PropTypes.bool,
  isGuidance: PropTypes.bool,
  prgmGenre: PropTypes.string,
  subGenre: PropTypes.string,
  progmButtonLink: PropTypes.string,
  programmeTab: PropTypes.bool,
  liveTab: PropTypes.bool,
  hasInformation: PropTypes.bool,
  playing: PropTypes.bool,
  competitionText: PropTypes.string,
  informationText: PropTypes.string,
  hasCompetitionText: PropTypes.bool,
  isVisible: PropTypes.bool,
  autoplayShortform: PropTypes.shape({
    videoId: PropTypes.number,
    drmEnabled: PropTypes.bool,
  }),
  inViewScroll: PropTypes.bool,
  userPaused: PropTypes.bool,
  firstRotation: PropTypes.bool,
  isStvLiveProg: PropTypes.bool,
  liveProgramme: PropTypes.shape({
    title: PropTypes.string,
    guid: PropTypes.string,
  }),
  showMyListButton: PropTypes.bool,
  animateCTA: PropTypes.bool,
  isFirstRender: PropTypes.bool,
}

HeroCard.defaultProps = {
  el: 'li',
  progmImages: {},
  programmeDetails: {},
  webHeroImage: {},
  mobileHeroImage: {},
  large: false,
  title: '',
  titleTextImage: {},
  overlay: '',
  hasHoverOverlay: false,
  newWindow: false,
  addClasses: [],
  link: null,
  linkGuid: null,
  sponsorship: null,
  forceAds: false,
  svodExclusive: false,
  svodProhibited: false,
  vipExclusive: false,
  vipProhibited: false,
  specialEvent: false,
  recoID: null,
  rank: null,
  ctaButtonLink: null,
  ctaButtonText: null,
  ctaButtonType: null,
  ctaText: null,
  live: false,
  index: null,
  poster: false,
  page: null,
  showCTA: false,
  name: '',
  longDescription: '',
  progmButton: '',
  availableEpisodes: '',
  audioDescribed: false,
  visuallySigned: false,
  isSubtitle: false,
  isGuidance: false,
  prgmGenre: '',
  subGenre: '',
  progmButtonLink: '',
  programmeTab: false,
  liveTab: false,
  hasInformation: false,
  playing: false,
  competitionText: '',
  informationText: '',
  hasCompetitionText: false,
  isVisible: true,
  autoplayShortform: {},
  inViewScroll: true,
  userPaused: false,
  firstRotation: true,
  isStvLiveProg: false,
  liveProgramme: {},
  showMyListButton: true,
  animateCTA: true,
  isFirstRender: false,
}

export default HeroCard
